import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  TextField,
  Chip,
  Radio,
  Button,
  makeStyles,
} from "@material-ui/core";
import { withStyles } from "@material-ui/styles";
import { isBrowser } from "react-device-detect";
import { withRouter } from "react-router-dom";
import { compose } from "recompose";
import { Clear } from "@material-ui/icons";
import { CustomTextField } from "../../../../../../common/CustomTextField";
import SubmitCancelBtn from "../../../../../../common/SubmitCancelBtn";
import Properties from "../../../../../../../properties/Properties";
import Snackbar from "../../../../../../common/Snackbar";
import { updateQuizInChallenge } from "../../../../../../../apis/eventService";

const QuizEditForm = ({
  selectedQuizItem,
  onUpdateSuccess,
  onCancel,
  classes,
}) => {
  // State Management
  const [quizText, setQuizText] = useState("");
  const [options, setOptions] = useState([]);

  // UI States
  const [inputView, setInputView] = useState(false); // Toggle "Add Option" input
  const [optionInput, setOptionInput] = useState(""); // Text for new option
  const [quizFieldError, setQuizFieldError] = useState(false);

  // Initialize state when selectedQuizItem loads
  useEffect(() => {
    if (selectedQuizItem) {
      setQuizText(selectedQuizItem.quiz || "");
      setOptions(selectedQuizItem.options || []);
    }
  }, [selectedQuizItem]);

  // --- Handlers ---

  const handleDeleteOption = (optionLabel) => {
    const updatedOptions = options.filter(
      (item) => item.option !== optionLabel,
    );
    setOptions(updatedOptions);
  };

  const onAddOption = () => {
    setInputView(true);
    setOptionInput("");
  };

  const saveOption = () => {
    if (!optionInput.trim()) return;
    const newOption = {
      id: null,
      option: optionInput,
      isCorrect: false,
    };

    setOptions([...options, newOption]);
    setOptionInput("");
    setInputView(false);
  };

  const handleCorrectAnswerChange = (selectedOptionLabel) => {
    const updatedOptions = options.map((opt) => ({
      ...opt,
      isCorrect: opt.option === selectedOptionLabel,
    }));
    setOptions(updatedOptions);
  };

  // --- API PARAM CONSTRUCTION ---
  const handleUpdate = () => {
    if (!quizText || options.length < 2) {
      Snackbar.show({
        variant: "error",
        message: "Please enter a question and at least 2 options.",
      });
      return;
    }

    // 1. Construct the Params
    const requestBody = {
      id: selectedQuizItem.id,
      quiz: quizText,
      options: options.map((opt) => ({
        id: opt.id, // May be null for new options
        option: opt.option,
        isCorrect: opt.isCorrect,
      })),
    };

    updateQuizInChallenge({ data: requestBody }).then((res) => {
      if (res?.status === 200 && res?.data?.code !== "400") {
        Snackbar.show({
          variant: "success",
          message: "Quiz updated successfully!",
        });
        onUpdateSuccess();
      } else {
        Snackbar.show({
          variant: "error",
          message: "Failed to update quiz. Please try again.",
        });
      }
    });
  };

  return (
    <div style={{ padding: "20px" }}>
      <Grid container spacing={1} style={{ padding: "10px 5px" }}>
        {/* Quiz Question Input */}
        <Grid
          item
          xs={12}
          style={{ display: "flex", padding: "6px 0", alignItems: "center" }}
        >
          <Typography style={{ width: "15%" }}>
            <span className={classes.labelCss}>
              {Properties.question_label}
              <span className={classes.mandatoryCss}>*</span> : &nbsp;
            </span>
          </Typography>
          <CustomTextField
            className={
              quizFieldError ? classes.fieldErrorCss : classes.fieldCss
            }
            placeholder={Properties.quizQuestion_placeholder}
            value={quizText}
            handleChange={() => (e) => {
              setQuizText(e.target.value);
              setQuizFieldError(false);
            }}
          />
        </Grid>

        {/* Options Management */}
        <Grid item xs={12} style={{ padding: "6px 0", display: "flex" }}>
          <Typography style={{ width: "15%" }}>
            <span className={classes.labelCss}>
              {Properties.option_label}
              <span className={classes.mandatoryCss}>*</span> : &nbsp;
            </span>
          </Typography>
          <div
            className={classes.optionsCss}
            style={{ flexWrap: "wrap", width: "85%" }}
          >
            {options.map((item, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "2px",
                }}
              >
                <TextField
                  variant="outlined"
                  size="small"
                  className={classes.fieldCss}
                  value={item.option}
                  style={{
                    width: "100px",
                    marginRight: "5px",
                    borderRadius: "4px",
                  }}
                  // Update the specific option text in the state array
                  onChange={(e) => {
                    const updatedOptions = [...options];
                    updatedOptions[index].option = e.target.value;
                    setOptions(updatedOptions);
                  }}
                />

                {/* Show delete only if it's a new option (no ID) */}
                {!item.id && (
                  <Clear
                    style={{
                      cursor: "pointer",
                      color: "red",
                      fontSize: "18px",
                    }}
                    onClick={() => handleDeleteOption(item.option)}
                  />
                )}
              </div>
            ))}
            {/* {options.map((item, index) => (
              <div key={index} style={{ padding: "2px" }}>
                <Chip
                  label={item.option}
                  // Only show delete functionality if the item is new (id is null/undefined)
                  onDelete={
                    !item.id ? () => handleDeleteOption(item.option) : undefined
                  }
                  deleteIcon={
                    !item.id ? <Clear /> : <div style={{ display: "none" }} />
                  }
                  color={item.isCorrect ? "primary" : "default"} // Highlight correct option visually
                  variant="outlined"
                />
              </div>
            ))} */}

            {inputView ? (
              <div style={{ display: "flex", alignItems: "center" }}>
                <TextField
                  value={optionInput}
                  onChange={(e) => setOptionInput(e.target.value)}
                  placeholder={Properties.option_placeholder}
                  size="small"
                  variant="outlined"
                  style={{ width: "150px" }}
                />
                <SubmitCancelBtn
                  onClick={saveOption}
                  label={Properties.add_label}
                  style={{ width: "80px", marginLeft: "8px" }}
                  type="submit"
                />
                <Button size="small" onClick={() => setInputView(false)}>
                  X
                </Button>
              </div>
            ) : (
              <SubmitCancelBtn
                onClick={onAddOption}
                label={Properties.addOption_label}
                style={{ width: "121px" }}
                type="submit"
              />
            )}
          </div>
        </Grid>

        {/* Expected Answer Selection (Radio style) */}
        <Grid item xs={12} style={{ padding: "6px 0", display: "flex" }}>
          <Typography style={{ width: "15%" }}>
            <span className={classes.expectedAnwerLabel}>
              {Properties.correct_answer_label}
              <span className={classes.mandatoryCss}>*</span> :
            </span>
          </Typography>
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {options.length > 0 ? (
              options.map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginRight: "15px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleCorrectAnswerChange(item.option)}
                >
                  <Radio
                    checked={item.isCorrect || false}
                    onChange={() => handleCorrectAnswerChange(item.option)}
                    value={item.option}
                    color="primary"
                    size="small"
                  />
                  <Typography variant="body2">{item.option}</Typography>
                </div>
              ))
            ) : (
              <Typography style={{ color: "#727272", fontStyle: "italic" }}>
                Add options to select the correct answer
              </Typography>
            )}
          </div>
        </Grid>

        {/* Action Buttons */}
        <Grid
          item
          xs={12}
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "center",
            gap: "10px",
          }}
        >
          <SubmitCancelBtn
            style={{ width: "121px" }}
            type="submit"
            onClick={onCancel}
            label={Properties.CancelLabel}
          />
          <SubmitCancelBtn
            label={Properties.update_label}
            style={{ width: "121px" }}
            type="submit"
            onClick={handleUpdate}
          />
        </Grid>
      </Grid>
    </div>
  );
};

const styles = (theme) => ({
  optionsCss: {
    display: "flex",
    flex: "flex-wrap",
  },
  expectedAnwerLabel: {
    color: "#727272",
    width: "fit-Content",
  },
  labelCss: {
    color: "#727272",
  },
  groupLabelCss: {
    fontSize: "20px",
    color: "rgb(8, 69, 108)",
  },
  mandatoryCss: { color: "red" },
  fieldCss: {
    border: "1px solid #08456C",
    height: "40px", //"45%",
    maxHeight: "40px",
    minHeight: "40px",
    width: isBrowser ? "360px" : "160px",
  },
  fieldErrorCss: {
    border: "1px solid red",
    height: "40px",
    maxHeight: "40px",
    minHeight: "40px",
    width: isBrowser ? "360px" : "160px",
  },
  title: {
    color: "rgb(8, 69, 108)",
  },

  option: {
    border: "1px solid #727272",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "10px",
    paddingBottom: "10px",
    // paddingRight: "",
  },
});

export default compose(
  withStyles(styles, { withTheme: true }),
  withRouter,
)(QuizEditForm);
